<script lang="ts">
  import DashCard from '../shared/DashCard.svelte';
  import { Camas, type Cama } from '../../../lib/api';

  export type CamaLite = Pick<Cama, 'id' | 'numero' | 'estado' | 'servicio' | 'sala'>;

  let {
    camas: camasProp,
    onCamaClick,
    title = 'Mapa de camas',
  }: {
    camas?: CamaLite[];
    onCamaClick?: (c: CamaLite) => void;
    title?: string;
  } = $props();

  let camas = $state<CamaLite[]>([]);
  let error = $state<string | null>(null);
  let sel = $state<CamaLite | null>(null);

  function clase(estado: string) {
    const e = estado.toUpperCase();
    if (e === 'DISPONIBLE' || e === 'LIMPIA') return 'libre';
    if (e === 'SUCIA' || e === 'ASIGNADA') return 'limpieza';
    if (e === 'MANTENIMIENTO' || e === 'BLOQUEADA') return 'mantenimiento';
    return 'ocupada';
  }

  /** Agrupa camas por área: número → piso, URG → urgencias, sino por servicio. */
  function areaDe(c: CamaLite): string {
    if (c.servicio && c.servicio.trim()) return c.servicio.trim();
    const n = c.numero.toUpperCase();
    if (n.startsWith('URG')) return 'Urgencias';
    if (n.startsWith('UCI') || n.startsWith('UTI')) return 'Cuidados intensivos';
    const m = /^(\d)/.exec(c.numero);
    if (m) return `Piso ${m[1]}`;
    return 'General';
  }

  const grupos: [string, CamaLite[]][] = $derived.by(() => {
    const map = new Map<string, CamaLite[]>();
    for (const c of camas) {
      const k = areaDe(c);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(c);
    }
    return [...map.entries()];
  });

  async function cargar() {
    if (camasProp?.length) {
      camas = camasProp;
      return;
    }
    error = null;
    try {
      camas = await Camas.list();
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  function click(c: CamaLite) {
    sel = c;
    onCamaClick?.(c);
  }

  $effect(() => {
    if (camasProp) camas = camasProp;
    else cargar();
  });
</script>

<DashCard {title} hint="{camas.length} camas">
  {#snippet actions()}
    <button type="button" class="btn-sm" onclick={cargar}>↻</button>
  {/snippet}
  {#if error}<p class="err">{error}</p>{/if}
  {#if camas.length === 0 && !error}
    <p class="empty">Sin camas registradas. Cree camas desde el módulo Camas.</p>
  {:else}
    {#each grupos as [area, list] (area)}
      <div class="area">
        <div class="area-label">
          <span>{area}</span>
          <small>{list.length} camas</small>
        </div>
        <div class="dash-camas">
          {#each list as c (c.id ?? c.numero)}
            <button
              type="button"
              class="dash-cama {clase(c.estado)}"
              class:sel={sel?.numero === c.numero}
              title="{c.numero} · {c.estado}{c.servicio ? ' · ' + c.servicio : ''}"
              onclick={() => click(c)}
            >{c.numero}</button>
          {/each}
        </div>
      </div>
    {/each}
    <div class="dash-leyenda">
      <span><i class="dash-dot" style="background:#30a46c"></i> Libre</span>
      <span><i class="dash-dot" style="background:#e5484d"></i> Ocupada</span>
      <span><i class="dash-dot" style="background:#f5a623"></i> Limpieza / asignada</span>
      <span><i class="dash-dot" style="background:#8b8fa3"></i> Mantenimiento</span>
    </div>
    {#if sel}
      <p class="detail"><strong>{sel.numero}</strong> — {sel.estado}{#if sel.servicio} · {sel.servicio}{/if}{#if sel.sala} · sala {sel.sala}{/if}</p>
    {/if}
  {/if}
</DashCard>

<style>
  .btn-sm { border: 1px solid var(--border); background: var(--bg-surface); border-radius: 6px; cursor: pointer; padding: 0.15rem 0.45rem; font-size: 0.78rem; }
  .err, .empty { margin: 0; font-size: 0.85rem; color: var(--text-secondary); }
  .err { color: var(--danger); }
  .detail { margin: 0.65rem 0 0; font-size: 0.82rem; color: var(--text-secondary); }
  .dash-cama.sel { outline: 2px solid var(--brand); outline-offset: 2px; }
  .area { margin-bottom: 0.85rem; }
  .area:last-child { margin-bottom: 0; }
  .area-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.4rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-secondary);
  }
  .area-label small { font-weight: 500; opacity: 0.7; }
</style>
